import React from 'react';
import { PlayerStats } from '../../types/player';
import { 
  calculateGoalInvolvementPer90, 
  calculateDisciplineRiskIndex, 
  calculateMinutesPerGoal, 
  calculateMinutesPerAssist,
  formatStat
} from '../../utils/playerAdvancedStatsUtils';
import { useKYPSearch } from '../../lib/kyp-search';

interface PlayerCareerStatsProps {
  playerStats: PlayerStats[] | null;
  isLoading: boolean;
}

export const PlayerCareerStats: React.FC<PlayerCareerStatsProps> = ({
  playerStats,
  isLoading
}) => {
  // Get pre-calculated career stats from context
  const { playerCareerStats } = useKYPSearch();
  
  // Use pre-calculated career stats from context if available, otherwise calculate locally
  // This ensures backward compatibility and provides a fallback
  const careerStats = React.useMemo(() => {
    // If we have pre-calculated career stats from context, use those
    if (playerCareerStats) {
      return playerCareerStats;
    }
    
    // Otherwise, calculate locally as a fallback
    if (!playerStats || playerStats.length === 0) {
      return null;
    }

    // Aggregate all stats
    return playerStats.reduce((acc, stat) => {
      return {
        ...acc,
        appearances: acc.appearances + stat.appearances,
        goals: acc.goals + stat.goals,
        assists: acc.assists + stat.assists,
        yellow_cards: acc.yellow_cards + stat.yellow_cards,
        red_cards: acc.red_cards + stat.red_cards,
        minutes_played: acc.minutes_played + stat.minutes_played
      };
    }, {
      appearances: 0,
      goals: 0,
      assists: 0,
      yellow_cards: 0,
      red_cards: 0,
      minutes_played: 0
    } as Partial<PlayerStats>);
  }, [playerStats, playerCareerStats]);

  if (isLoading && !careerStats) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#4E46FF] border-t-transparent"></div>
      </div>
    );
  }

  if (!careerStats) {
    return (
      <p className="text-center text-[#CECFD2] py-4">No career statistics available</p>
    );
  }

  return (
    <div className="space-y-4">
      <h4 className="text-xl font-bold text-[#CECFD2] mb-3">Career Statistics</h4>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
          <p className="text-sm text-[#CECFD2]">Total Appearances</p>
          <p className="text-xl font-bold text-[#CECFD2]">{careerStats.appearances}</p>
        </div>
        <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
          <p className="text-sm text-[#CECFD2]">Total Goals</p>
          <p className="text-xl font-bold text-[#CECFD2]">{careerStats.goals}</p>
        </div>
        <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
          <p className="text-sm text-[#CECFD2]">Total Assists</p>
          <p className="text-xl font-bold text-[#CECFD2]">{careerStats.assists}</p>
        </div>
        <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
          <p className="text-sm text-[#CECFD2]">Total Minutes</p>
          <p className="text-xl font-bold text-[#CECFD2]">
            {careerStats.minutes_played ? careerStats.minutes_played.toLocaleString() : '0'}
          </p>
        </div>
      </div>
      
      <h4 className="text-xl font-bold text-[#CECFD2] mb-3 mt-6">Career Advanced Statistics</h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
          <p className="text-sm text-[#CECFD2]">Goal Involvement per 90</p>
          <p className="text-xl font-bold text-[#CECFD2]">
            {formatStat(calculateGoalInvolvementPer90(careerStats as PlayerStats))}
          </p>
        </div>
        <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
          <p className="text-sm text-[#CECFD2]">Discipline Risk Index</p>
          <p className="text-xl font-bold text-[#CECFD2]">
            {formatStat(calculateDisciplineRiskIndex(careerStats as PlayerStats))}
          </p>
        </div>
        <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
          <p className="text-sm text-[#CECFD2]">Minutes per Goal</p>
          <p className="text-xl font-bold text-[#CECFD2]">
            {formatStat(calculateMinutesPerGoal(careerStats as PlayerStats))}
          </p>
        </div>
        <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
          <p className="text-sm text-[#CECFD2]">Minutes per Assist</p>
          <p className="text-xl font-bold text-[#CECFD2]">
            {formatStat(calculateMinutesPerAssist(careerStats as PlayerStats))}
          </p>
        </div>
      </div>
    </div>
  );
};